"use client";

/**
 * Client gate for the streamed broad category (Approach A, Phase 5, 2026-08-17).
 *
 * /compare (a Server Component) renders this in place of the blocking
 * resolveBroad->FastResults path when NEXT_PUBLIC_BROAD_STREAM_ENABLED is on. It
 * owns the routing that the blocking path did server-side, now that the mode is
 * only known once the stream classifies:
 *   - broad         -> BroadStreamView renders the live-building list (here).
 *   - ask           -> AskExact, exactly as the blocking path returned it.
 *   - exact / out_of_scope, or a pre-picks stream failure -> the clarify ->
 *     ClarificationGrid / ComparisonStream flow, mounted IN PLACE (see below).
 *
 * 2026-08-28 blank-screen fix: the fallthrough used to router.replace to
 * /compare?q=..&nostream=1 - a SECOND server pass that re-ran resolveQuery +
 * auth + a 3-5s clarify LLM call (measured 3.25-5.19s for brand-new queries)
 * while BroadStreamView rendered null. Next does not re-mount loading.tsx for a
 * same-segment searchParams navigation, so NOTHING replaced that null until the
 * whole second pass finished: the owner-reported 1-3s blank screen on every
 * typed new product search. Now /compare kicks clarify off eagerly (un-awaited,
 * settled-wrapped) and streams the promise here; on a non-broad classification
 * this gate mounts the SAME flow the nostream pass produced - ClarificationGrid
 * when clarify wants chips, ComparisonStream otherwise - directly, with the
 * searching scene as the Suspense fallback for whatever is left of the clarify
 * call (which overlapped the classification, so usually little). One
 * navigation, no second server pass, no frame without a painted surface.
 *
 * A mid-stream failure AFTER the picks are shown does NOT fall through (the
 * shopper keeps the list they can already see) - BroadStreamView only signals
 * onError before any picks arrive.
 */

import { Suspense, use, useState } from "react";

import type { ClarifyResponse } from "@/lib/api/clarify";
import { SEARCH_SCENE_ENABLED } from "@/lib/flags";

import { AskExact } from "./AskExact";
import { BroadStreamView } from "./BroadStreamView";
import { ClarificationGrid } from "./ClarificationGrid";
import { ComparisonShell } from "./ComparisonShell";
import { ComparisonStream } from "./ComparisonStream";
import { RedirectToSaved } from "./RedirectToSaved";
import { SearchingSceneOverlay } from "./SearchingSceneOverlay";

/** The eagerly-started clarify call, settled server-side so it can never
 *  reject in the client (`ok` = the response, `err` = fall through to the
 *  stream without intent - the exact ClarifyError behaviour of the old
 *  nostream server pass). */
export type GateClarify = { ok: ClarifyResponse } | { err: string };

interface Props {
  query: string;
  postcode: string;
  userId: string | null;
  clarify: Promise<GateClarify>;
}

/** The one continuation surface: the same scene loading.tsx shows (or the
 *  skeleton shell with the scene flag off), so every wait in this flow is the
 *  same uninterrupted scene - never a blank frame. */
function ContinuationScene() {
  return SEARCH_SCENE_ENABLED ? (
    <SearchingSceneOverlay loaded={0} failed={0} total={0} isDone={false} />
  ) : (
    <ComparisonShell />
  );
}

/** Suspends on the eagerly-started clarify, then renders exactly what the old
 *  /compare?nostream=1 server pass rendered: the chip grid when clarify wants
 *  clarification, otherwise the streaming search (with the parsed intent when
 *  clarify succeeded, without it when it failed). */
function ClarifyFallthrough({ query, postcode, userId, clarify }: Props) {
  const settled = use(clarify);
  if ("ok" in settled) {
    const c = settled.ok;
    // B-402 (9 Oct 2026, owner-approved): the alignment check found the
    // saved page that IS this product. Navigate there; no search is paid.
    if (c.saved_page?.slug && c.saved_page.page_kind !== "list") {
      return <RedirectToSaved target={`/c/${c.saved_page.slug}`} />;
    }
    if (c.needs_clarification && c.chips.length > 0) {
      return (
        <ClarificationGrid
          rawQuery={query}
          clarifyingQuestion={c.clarifying_question}
          chips={c.chips}
          postcode={postcode}
        />
      );
    }
    return (
      <ComparisonStream
        key={query}
        query={query}
        postcode={postcode}
        initialIntent={c.intent}
        userId={userId}
      />
    );
  }
  return (
    <ComparisonStream key={query} query={query} postcode={postcode} userId={userId} />
  );
}

export function BroadStreamGate({ query, postcode, userId, clarify }: Props) {
  const [ask, setAsk] = useState(false);
  const [fellThrough, setFellThrough] = useState(false);

  if (ask) return <AskExact query={query} />;

  if (fellThrough) {
    return (
      <Suspense fallback={<ContinuationScene />}>
        <ClarifyFallthrough
          query={query}
          postcode={postcode}
          userId={userId}
          clarify={clarify}
        />
      </Suspense>
    );
  }

  return (
    <BroadStreamView
      query={query}
      postcode={postcode}
      userId={userId}
      onNonBroad={(p) => (p.mode === "ask" ? setAsk(true) : setFellThrough(true))}
      onError={() => setFellThrough(true)}
    />
  );
}
